import type { LayerConfig } from '../../api/types';
import {
  kindOf,
  symbologyLegend,
  withLabel,
  withSymbology,
  type LabelStyle,
  type Symbology,
} from '../../lib/layers-pro/symbology';
import {
  loadZoomRanges,
  saveZoomRanges,
  dataStart,
  isDefault,
  styleForRange,
  type ZoomRange,
} from '../../lib/layers-pro/zoomRange';
import { browserStorage } from '../../lib/visibilityMemory';
import { useMapStore } from '../../state/mapStore';
import { useLayersPro } from './store';

/**
 * Edits made with Layers Pro's Symbology, Labels and Zoom levels editors, written into the layer's loaded config so the
 * map, the legend, the tree's scale check and a print all follow them. Each layer's own config is kept from its first
 * edit; the edits are separate, so resetting one leaves the others. Zoom levels are remembered per project in this
 * browser and put back as the app loads (restoreZoomRanges).
 */

interface Edits {
  own: LayerConfig;
  symbology: Symbology | null;
  label: { style: LabelStyle; latitude: number } | null;
  zoom: ZoomRange | null;
}

const edits = new Map<string, Edits>();

function apply(layerId: string, change: (e: Edits) => void): void {
  const state = useMapStore.getState();
  const current = state.layers.get(layerId);
  if (!current) return;
  const e = edits.get(layerId) ?? { own: current, symbology: null, label: null, zoom: null };
  change(e);
  const kind = kindOf(e.own);
  let style = e.own.style.map((l) => ({ ...l }));
  let legend = e.own.legend;
  if (e.symbology) {
    style = withSymbology(style, kind, e.symbology);
    legend = symbologyLegend(e.own.title, kind, e.symbology);
  }
  if (e.label) style = withLabel(style, layerId, kind, e.label.style, e.label.latitude);
  if (e.zoom) style = styleForRange(style, e.own.zoom, e.zoom);
  if (e.symbology || e.label || e.zoom) edits.set(layerId, e);
  else edits.delete(layerId);
  const layers = new Map(state.layers);
  layers.set(layerId, { ...e.own, style, legend, zoom: e.zoom ?? e.own.zoom });
  useMapStore.setState({ layers });
}

export function setSymbology(layerId: string, s: Symbology | null): void {
  apply(layerId, (e) => {
    e.symbology = s;
  });
}

export function setLabelStyle(layerId: string, style: LabelStyle | null, latitude: number): void {
  apply(layerId, (e) => {
    e.label = style ? { style, latitude } : null;
  });
}

/** The layer's own config (before any edit), for what an editor opens on. */
export function ownLayer(layerId: string): LayerConfig | undefined {
  return edits.get(layerId)?.own ?? useMapStore.getState().layers.get(layerId);
}

/** The zoom a layer's tiles start at, as the manifest says (its own minimum until the manifest has loaded). */
export function tilesStart(layerId: string): number {
  const own = ownLayer(layerId)?.zoom ?? { min: 0 };
  const shown = useMapStore.getState().manifest?.layers.find((m) => m.layer_id === layerId);
  return dataStart(own, shown?.tiles_minzoom);
}

const projectScope = (): string => useMapStore.getState().config?.project.id ?? '';

/** Remember every layer's zoom levels for this project, and tell Layers Pro how many there are (its Reset strip). */
function keepZoomRanges(): void {
  const ranges = new Map<string, ZoomRange>();
  for (const [id, e] of edits) if (e.zoom) ranges.set(id, e.zoom);
  saveZoomRanges(browserStorage(), projectScope(), ranges);
  useLayersPro.setState({ zoomRanges: ranges.size });
}

/**
 * Set layers' zoom levels (null: their own again). A range is kept as set, so one remembered from before the manifest
 * loads is not cut to the layer's own minimum; further out than its tiles start, the map just has nothing to draw.
 */
export function setZoomRange(layerIds: readonly string[], range: ZoomRange | null): void {
  for (const id of layerIds) {
    apply(id, (e) => {
      e.zoom = range && !isDefault(range, e.own.zoom) ? range : null;
    });
  }
  keepZoomRanges();
}

/** A layer's zoom range now (its own unless set). */
export function zoomRangeOf(layerId: string): ZoomRange | undefined {
  return edits.get(layerId)?.zoom ?? ownLayer(layerId)?.zoom;
}

/** Every layer back to its own zoom levels, and nothing remembered for this project (Layers Pro's Reset to defaults). */
export function resetZoomRanges(): void {
  setZoomRange(
    [...edits].filter(([, e]) => e.zoom).map(([id]) => id),
    null,
  );
}

/** Put back the zoom levels this browser remembers for the project, once its layers have loaded. */
export function restoreZoomRanges(): void {
  const kept = loadZoomRanges(browserStorage(), projectScope());
  const layers = useMapStore.getState().layers;
  for (const [id, range] of kept) if (layers.has(id)) setZoomRange([id], range);
  keepZoomRanges();
}

/** The edits of a layer now, for the editors to open on. */
export function layerEdits(layerId: string): { symbology: Symbology | null; label: LabelStyle | null } {
  const e = edits.get(layerId);
  return { symbology: e?.symbology ?? null, label: e?.label?.style ?? null };
}
